/**
 * Serialization of selection filters: runtime format (numeric IDs) → portable format (category names).
 * Used when copying selections to clipboard or saving to disk. See ./index.ts for the feature-level
 * overview and the "manual diff" design rationale.
 */

import { convertColumnDataToDisplayString } from '../../../features/visual-analytics/helpers/misc';
import { getColumnInfo } from '../../analyticsDataSlice';
import { getDataTable } from '../../../features/visual-analytics/charts/chartDataBindings';
import { TpCanonicalTypeInfo, TpDataContext } from '../../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepCategorical,
  TpEnSelectionFilterStepDualCategorical,
  TpEnSelectionFilterStepIntersections,
  TpEnSelectionFilterTypes,
} from '../interfaces';
import {
  TpCategoricalStepSchema,
  TpDualCategoricalStepSchema,
  TpIntersectionsStepSchema,
  TpSelectionFilterStep,
  TpCanonicalTypeSelection,
  TpSelectionStoragePayloadV1,
} from './selectionFilterSerialization.schemas';
import { cloneSelectionFilter } from '../selectionUtils';
import { getSelectionItemsMapForFilter } from '../selectionEnFilters';

/**
 * Converts categorical step numeric IDs → category names
 */
function convertCategoricalStepToStorageFormat(
  step: TpEnSelectionFilterStepCategorical,
  dataContext: TpDataContext,
): TpCategoricalStepSchema {
  const { includedCount, ...serializableStep } = step;
  const dataTable = getDataTable(dataContext, step.dataTableId);
  const colInfo = getColumnInfo(dataTable, step.colId);

  const selectedCategoryNames = step.selectedCategoryIdList.map((catId) =>
    convertColumnDataToDisplayString(colInfo, catId),
  );

  const excludedCategoryNames = step.excludedCategoryIdList.map((catId) =>
    convertColumnDataToDisplayString(colInfo, catId),
  );

  return {
    ...serializableStep,
    selectedCategoryIdList: selectedCategoryNames,
    excludedCategoryIdList: excludedCategoryNames,
  };
}

/**
 * Converts dual categorical step numeric IDs → category names
 */
function convertDualCategoricalStepToStorageFormat(
  step: TpEnSelectionFilterStepDualCategorical,
  dataContext: TpDataContext,
): TpDualCategoricalStepSchema {
  const { includedCount, ...serializableStep } = step;
  const dataTable = getDataTable(dataContext, step.dataTableId);
  const colInfo1 = getColumnInfo(dataTable, step.colId1);
  const colInfo2 = getColumnInfo(dataTable, step.colId2);

  const selectedCells = step.selectedCellsList.map((cell) => ({
    val1: convertColumnDataToDisplayString(colInfo1, cell.val1),
    val2: convertColumnDataToDisplayString(colInfo2, cell.val2),
  }));

  return {
    ...serializableStep,
    selectedCellsList: selectedCells,
  };
}

/**
 * Converts intersections step 2D array of numeric IDs → category names
 */
function convertIntersectionsStepToStorageFormat(
  step: TpEnSelectionFilterStepIntersections,
  dataContext: TpDataContext,
): TpIntersectionsStepSchema {
  const { includedCount, ...serializableStep } = step;
  const dataTable = getDataTable(dataContext, step.dataTableId);
  const colInfo = getColumnInfo(dataTable, step.colId);

  const intersectionNames = step.intersections.map((intersection) =>
    intersection.map((catId) => convertColumnDataToDisplayString(colInfo, catId)),
  );

  return {
    ...serializableStep,
    intersections: intersectionNames,
  };
}

/**
 * Converts a runtime TpEnSelectionFilterStep into a portable TpSelectionFilterStep for storage.
 * This involves stripping out any runtime-only properties and ensuring the structure matches the
 * expected storage format.
 */
function convertStepToStorageFormat(step: TpEnSelectionFilterStep, dataContext: TpDataContext): TpSelectionFilterStep {
  switch (step.type) {
    case TpEnSelectionFilterTypes.CATEGORICAL:
      return convertCategoricalStepToStorageFormat(step as TpEnSelectionFilterStepCategorical, dataContext);
    case TpEnSelectionFilterTypes.DUALCATEGORICAL:
      return convertDualCategoricalStepToStorageFormat(step as TpEnSelectionFilterStepDualCategorical, dataContext);

    case TpEnSelectionFilterTypes.INTERSECTIONS:
      return convertIntersectionsStepToStorageFormat(step as TpEnSelectionFilterStepIntersections, dataContext);

    // Note on MultiLevelCat: categorical keys are already stored as strings, so no conversion is needed.
    case TpEnSelectionFilterTypes.MULTILEVELCAT:
    case TpEnSelectionFilterTypes.NUMERICALRANGE:
    case TpEnSelectionFilterTypes.LASSO2D:
    case TpEnSelectionFilterTypes.MANUAL:
    case TpEnSelectionFilterTypes.IMPORT: {
      // Strip out non-serializable or unnecessary runtime properties from individual steps
      const { includedCount, ...serializableStep } = step;
      return serializableStep as TpSelectionFilterStep;
    }
  }
}

/** Converts one runtime filter to its portable storage representation. */
export function mapSelectionFilterToStorageDTO(
  dataContext: TpDataContext,
  filter: TpActiveEnSelectionFilter,
): { steps: TpSelectionFilterStep[] } {
  return { steps: filter.steps.map((step) => convertStepToStorageFormat(step, dataContext)) };
}

/**
 * Computes the symmetric difference (XOR) between two item-id membership maps: every id whose
 * membership disagrees between `actualMap` and `baselineMap`. Used both to *capture* a manual
 * diff (actual live map vs. a freshly-computed steps-only map) and is replayed unchanged on the
 * target side.
 */
function computeManualDiffIds(
  actualMap: { [itemId: string]: boolean } | null,
  baselineMap: { [itemId: string]: boolean },
): string[] {
  const actual = actualMap ?? {};
  const allIds = new Set([...Object.keys(actual), ...Object.keys(baselineMap)]);
  const diff: string[] = [];
  for (const id of allIds) if (!!actual[id] !== !!baselineMap[id]) diff.push(id);
  return diff;
}

/**
 * Computes the list of item IDs that are manually toggled (i.e., whose membership disagrees with the
 * computed membership from the filter steps). This is used to capture the "manual diff" for storage.
 * This is done by comparing the canonical type's `selectedItemsMap`
 * (actual live selection) with the computed selection from the filter steps.
 */
function getManualDiffIdsForFilter(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  finalItemsMap: { [itemId: string]: boolean } | null,
  selectionFilter: TpActiveEnSelectionFilter | null,
): string[] {
  const baselineMap =
    !selectionFilter || selectionFilter.steps.length == 0
      ? {}
      : getSelectionItemsMapForFilter(dataContext, canonicalTypeInfo, selectionFilter.steps);
  const manualDiffIds = computeManualDiffIds(finalItemsMap, baselineMap);
  return manualDiffIds;
}

export function mapCanonicalTypeInfoToSelectionDTO(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
): TpCanonicalTypeSelection {
  const selectionFilter = cloneSelectionFilter(canonicalTypeInfo.selectionFilter);
  const restrictionFilter = cloneSelectionFilter(canonicalTypeInfo.restrictionFilter);
  const manualToggleIds = getManualDiffIdsForFilter(
    dataContext,
    canonicalTypeInfo,
    canonicalTypeInfo.selectedItemsMap,
    selectionFilter,
  );
  const restrictionManualToggleIds = getManualDiffIdsForFilter(
    dataContext,
    canonicalTypeInfo,
    canonicalTypeInfo.restrictedItemsMap,
    restrictionFilter,
  );

  return {
    selectionFilter: mapSelectionFilterToStorageDTO(dataContext, selectionFilter!),
    manualToggleIds,
    restrictionFilter: restrictionFilter ? mapSelectionFilterToStorageDTO(dataContext, restrictionFilter) : null,
    restrictionManualToggleIds,
    canonicalTypeName: canonicalTypeInfo.name,
  };
}

function mapCanonicalTypeInfoToSelection(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
): TpCanonicalTypeSelection | null {
  const selection = mapCanonicalTypeInfoToSelectionDTO(dataContext, canonicalTypeInfo);
  const hasSelection = selection.selectionFilter.steps.length > 0 || selection.manualToggleIds.length > 0;
  const hasRestriction =
    !!selection.restrictionFilter &&
    (selection.restrictionFilter.steps.length > 0 || selection.restrictionManualToggleIds.length > 0);
  return hasSelection || hasRestriction ? selection : null;
}

/**
 * Builds the full-data-context selection snapshot to be copied to the clipboard. For each
 * canonical type with a non-empty selection and/or restriction, captures the rule-based `steps`
 * verbatim plus the "manual diff" needed to reconstruct any manual toggles (see module doc).
 */
export function mapReduxToSelectionDTO(dataContext: TpDataContext, assetType: string): TpSelectionStoragePayloadV1 {
  const canonicalTypeSelections = Object.values(dataContext.canonicalTypes).reduce(
    (acc, canonicalTypeInfo) => {
      const canonicalTypeSelection: TpCanonicalTypeSelection | null = mapCanonicalTypeInfoToSelection(
        dataContext,
        canonicalTypeInfo,
      );
      if (canonicalTypeSelection) {
        acc[canonicalTypeInfo.id] = canonicalTypeSelection;
      }
      return acc;
    },
    {} as Record<string, TpCanonicalTypeSelection>,
  );
  return {
    version: 1,
    sourceAssetId: dataContext.assetId,
    sourceAssetName: dataContext.name,
    assetType: assetType,
    canonicalTypeSelections,
  };
}
